/**
 * Player progression — level, XP and unlocks.
 *
 * ## This is not the scorestreak currency
 *
 * There are two counters in this game called "XP" and confusing them will produce
 * bugs that look like balance problems. They are:
 *
 *   - **`GameDirector.xp` / `Scorestreaks`** — *score*. Earned per kill, spent on the
 *     scorestreak ladder, and **reset on death**. Its whole job is to make a life
 *     feel valuable. It never leaves the match.
 *
 *   - **`Progression.xp` (this file)** — *career XP*. Earned per kill, never spent,
 *     never reset, **persisted to localStorage across matches and sessions**. Its job
 *     is to make the hundredth match feel like it counted.
 *
 * They are deliberately separate objects with separate lifetimes. This module never
 * reads or writes `GameDirector.xp`, and nothing here should ever be "unified" with
 * the streak ladder — a player who dies must lose their streak progress and keep
 * their level.
 *
 * ## The curve
 *
 * `cost(L) = round25(BASE + SCALE * L^POWER)`, strictly increasing by construction:
 * if quantisation ever ties two adjacent levels the later one is bumped, so "each
 * level costs more than the last" is a guarantee rather than a property of the
 * constants. Levels 1→6 fall inside one good match; 55 lands around match 53 at
 * ~4,000 XP/match. Total to 55 is 212,175 XP.
 *
 * ## What is stored
 *
 * Only lifetime XP and the equipped loadout. Level is *derived* from XP every time,
 * which means there is exactly one number that can be corrupted and no way for level
 * and XP to disagree with each other. A save that fails to parse, is from an older
 * shape, or contains nonsense is discarded silently and the player starts fresh —
 * never a thrown exception, never a broken game.
 *
 * Emits: `xp:gain { amount, reason, total }`, `level:up { level, unlocked[] }`,
 *        `loadout:set { primary, secondary }`.
 * Listens: `actor:death`, `player:death`, `game:state`.
 */

import {
  CLASSES,
  WEAPON_UNLOCKS,
  unlocksAtLevel,
  nextUnlockLevel,
} from './unlocks.data.js';

/**
 * Weapon definitions, imported for display names and stat bars only.
 * `weapons.data.js` is a pure data module with no imports of its own, which is the
 * only reason reaching for it here does not violate the "never import another
 * feature module" rule in spirit. Every access is guarded: a missing or renamed id
 * degrades the entry to `pending` rather than throwing.
 */
import { WEAPONS as WEAPONS_DATA } from '../weapons/weapons.data.js';

export const TUNING = {
  maxLevel: 55,

  /* --- curve --- */
  curveBase: 550,
  curveScale: 13.5,
  curvePower: 1.62,
  curveQuantum: 25, // costs are readable numbers, not 1743

  /* --- XP sources. Few and legible on purpose. --- */
  xpKill: 100,
  xpHeadshot: 50, // on top of the kill
  xpMultiStep: 50, // per extra kill inside the window: 2nd +50, 3rd +100 ...
  xpMultiMax: 250,
  multiWindow: 4.0, // s — matches the feel of the streak window, kept independent
  xpStreakStep: 100, // every N consecutive kills without dying
  streakEvery: 5,
  xpMatchWin: 500,

  /* --- persistence --- */
  saveKey: 'blackout.progression',
  saveVersion: 1,
  saveDebounce: 0.75, // s — kills come in bursts; don't hit disk on every one
};

/** Cost to go from `level` to `level + 1`. Index 0 and `maxLevel` are 0. */
const COST = buildCurve();
/** `CUMULATIVE[L]` = total career XP required to *be* level L. CUMULATIVE[1] = 0. */
const CUMULATIVE = buildCumulative(COST);

function buildCurve() {
  const out = new Array(TUNING.maxLevel + 1).fill(0);
  const { curveBase: b, curveScale: s, curvePower: p, curveQuantum: q } = TUNING;
  let prev = 0;
  for (let L = 1; L < TUNING.maxLevel; L++) {
    let c = Math.round((b + s * Math.pow(L, p)) / q) * q;
    // Strictly increasing is a design requirement, not a hope. Quantisation can tie
    // two adjacent low levels; bump rather than let the curve go flat.
    if (c <= prev) c = prev + q;
    out[L] = c;
    prev = c;
  }
  return out;
}

function buildCumulative(cost) {
  const out = new Array(TUNING.maxLevel + 2).fill(0);
  for (let L = 1; L < TUNING.maxLevel; L++) out[L + 1] = out[L] + cost[L];
  // One past the cap, so `xpIntoLevel` at max has a sane denominator.
  out[TUNING.maxLevel + 1] = out[TUNING.maxLevel];
  return out;
}

/* ---------------------------------------------------------------------------- *
 * Pure curve helpers — exported so the loadout screen and any test harness can ask
 * questions about the ladder without owning a Progression instance.
 * ---------------------------------------------------------------------------- */

/** XP required to go from `level` to `level + 1`. 0 at the cap. */
export function xpForLevel(level) {
  return COST[clampLevel(level)] ?? 0;
}

/** Total career XP required to reach `level`. */
export function xpToReach(level) {
  return CUMULATIVE[clampLevel(level)] ?? 0;
}

/** The level a given lifetime XP total corresponds to. */
export function levelForXp(xp) {
  const x = Number.isFinite(xp) ? Math.max(0, xp) : 0;
  // 55 entries: a linear walk is ~30 ns and clearer than a binary search.
  let L = 1;
  while (L < TUNING.maxLevel && x >= CUMULATIVE[L + 1]) L++;
  return L;
}

function clampLevel(level) {
  const n = Math.floor(Number(level));
  if (!Number.isFinite(n)) return 1;
  return Math.min(TUNING.maxLevel, Math.max(1, n));
}

/** The whole ladder as rows, for reporting and for the loadout screen's list. */
export function curveTable() {
  const rows = [];
  for (let L = 1; L <= TUNING.maxLevel; L++) {
    rows.push({ level: L, cost: COST[L], cumulative: CUMULATIVE[L], unlocks: unlocksAtLevel(L) });
  }
  return rows;
}

/* ---------------------------------------------------------------------------- */

export class Progression {
  constructor(ctx) {
    this.ctx = ctx;

    /** Lifetime career XP. The single source of truth; level is derived from it. */
    this.xp = 0;
    this.level = 1;

    /** Equipped weapon ids. `secondary` may be null. */
    this.primary = null;
    this.secondary = null;

    /** Per-match accounting for the post-match summary. Cleared on match start. */
    this.match = newMatchTally();

    /** Set by the debug harness only; makes every weapon selectable at any level. */
    this.debugUnlockAll = false;

    this._lastKillAt = -999;
    this._multi = 0;
    this._lifeStreak = 0;
    this._saveTimer = 0;
    this._dirty = false;

    this._load();
    this._resolveWeapons();
    this._ensureLoadout();
    this._bind();

    this._unregister = ctx.engine?.registerUpdate?.((dt) => this.update(dt), 'ui');

    // Career XP must survive a tab close mid-match; the debounce would otherwise
    // eat the last few kills.
    this._onHide = () => this._flush();
    window.addEventListener('pagehide', this._onHide);
    document.addEventListener('visibilitychange', this._onHide);
  }

  /* ------------------------------- weapons -------------------------------- */

  /**
   * Bind the unlock table to whatever weapons actually exist right now.
   *
   * The LMG and sniper rifle are being authored concurrently and may resolve to an
   * id nobody has agreed on yet, so each entry carries aliases and an entry that
   * matches nothing is kept and marked `pending` rather than dropped. A pending
   * weapon renders in the loadout screen and cannot be equipped. Nothing throws.
   */
  _resolveWeapons() {
    const defs = this.ctx?.weapons?.defs ?? this.ctx?.weapons?.WEAPONS ?? WEAPONS_DATA;

    this.weapons = WEAPON_UNLOCKS.map((w) => {
      let id = null;
      let def = null;
      if (defs) {
        for (const candidate of [w.id, ...(w.aliases ?? [])]) {
          if (defs[candidate]) {
            id = candidate;
            def = defs[candidate];
            break;
          }
        }
      }
      return {
        id: id ?? w.id,
        exists: !!def,
        pending: !def,
        classId: w.class,
        level: w.level,
        name: def?.name ?? w.fallbackName,
        className: def?.className ?? classNameFor(w.class),
        note: w.note,
        // A handful of numbers for the loadout screen's stat bars. All optional —
        // a pending weapon simply has none and the bars render empty.
        stats: def ? statsFor(def) : null,
      };
    });

    this.classes = CLASSES.map((c) => ({
      ...c,
      weapons: this.weapons.filter((w) => w.classId === c.id),
    }));
  }

  /** Is `weaponId` available to the player right now? */
  isUnlocked(weaponId) {
    const w = this.weapons.find((x) => x.id === weaponId);
    if (!w) return false;
    if (w.pending) return false;
    return this.debugUnlockAll || this.level >= w.level;
  }

  /** Weapons the player can actually equip, in table order. */
  availableWeapons() {
    return this.weapons.filter((w) => this.isUnlocked(w.id));
  }

  /* ------------------------------- loadout -------------------------------- */

  /**
   * Equip a loadout. Ids that are locked, pending or unknown are refused rather
   * than silently corrected, so a caller can tell the difference between "you can't
   * have that" and "done".
   */
  setLoadout(primary, secondary = this.secondary) {
    if (primary != null && !this.isUnlocked(primary)) return false;
    if (secondary != null && !this.isUnlocked(secondary)) secondary = null;
    if (secondary === primary) secondary = null;

    this.primary = primary ?? this.primary;
    this.secondary = secondary;
    this._dirty = true;
    // Written straight through rather than debounced. XP arrives in bursts and can
    // afford to wait; a loadout change is one deliberate keypress a minute and is
    // the single thing a player will notice not having stuck.
    this._flush();
    this._publishLoadout();
    return true;
  }

  /**
   * Tell the rest of the game. `WeaponSystem` does not consume `loadout:set` yet, so
   * the direct call is attempted first and the event is emitted either way — when
   * the lead wires the weapon system up, nothing here has to change.
   */
  _publishLoadout() {
    const payload = { primary: this.primary, secondary: this.secondary };
    try {
      this.ctx?.weapons?.setLoadout?.(this.primary, this.secondary);
    } catch (err) {
      // A half-built weapon system must not be able to break progression.
      console.warn('[progression] weapons.setLoadout failed; event still sent', err);
    }
    this.ctx?.events?.emit?.('loadout:set', payload);
  }

  /** Fall back to the first thing the player owns if the save named something gone. */
  _ensureLoadout() {
    const owned = this.availableWeapons();
    if (!owned.length) return;
    if (!this.primary || !this.isUnlocked(this.primary)) this.primary = owned[0].id;
    if (this.secondary && (!this.isUnlocked(this.secondary) || this.secondary === this.primary)) {
      this.secondary = null;
    }
    if (!this.secondary && owned.length > 1) {
      this.secondary = owned.find((w) => w.id !== this.primary)?.id ?? null;
    }
  }

  /* --------------------------------- XP ----------------------------------- */

  _bind() {
    const ev = this.ctx?.events;
    if (!ev) return;

    // Subscribed directly rather than routed through GameDirector: career XP and
    // streak score are different currencies and must not share a code path.
    // Was `actor !== player`, which read as "a kill is any death that is not
    // mine". The match answers the real question now, so a teammate's kill stops
    // paying into this player's career.
    ev.on('actor:death', ({ headshot, mine } = {}) => {
      if (!mine) return;
      this._onKill(!!headshot);
    });

    ev.on('player:death', () => {
      this._lifeStreak = 0;
      this._multi = 0;
      this.match.deaths++;
    });

    ev.on('game:state', ({ state } = {}) => {
      if (state === 'won') {
        this.addXp(TUNING.xpMatchWin, 'match');
        this.match.ended = true;
      } else if (state === 'dead') {
        this._lifeStreak = 0;
        this._multi = 0;
      }
    });
  }

  _onKill(headshot) {
    const now = this.ctx?.engine?.elapsed ?? performance.now() / 1000;

    this.match.kills++;
    this.addXp(TUNING.xpKill, 'kill');

    if (headshot) {
      this.match.headshots++;
      this.addXp(TUNING.xpHeadshot, 'headshot');
    }

    // Multi-kill: the second kill inside the window is worth +50, the third +100,
    // and so on to a cap. The window restarts on every kill, so a sustained spree
    // keeps escalating rather than resetting at a fixed boundary.
    this._multi = now - this._lastKillAt < TUNING.multiWindow ? this._multi + 1 : 0;
    this._lastKillAt = now;
    if (this._multi > 0) {
      const bonus = Math.min(TUNING.xpMultiMax, TUNING.xpMultiStep * this._multi);
      this.addXp(bonus, 'multikill');
    }

    // Life streak: a flat bonus every 5 kills without dying. Cheap to reason about
    // and it rewards exactly the thing the scorestreak ladder also rewards, which
    // keeps the two currencies pointing the same direction without sharing state.
    this._lifeStreak++;
    if (this._lifeStreak % TUNING.streakEvery === 0) {
      this.addXp(TUNING.xpStreakStep, 'streak');
    }
  }

  /**
   * Add career XP and level up as far as it takes us. Returns the XP actually
   * granted, which is 0 at the cap — a maxed player should not accumulate a hidden
   * overflow that suddenly matters if the cap is ever raised.
   */
  addXp(amount, reason = 'debug') {
    const n = Math.round(Number(amount));
    if (!Number.isFinite(n) || n === 0) return 0;
    if (this.level >= TUNING.maxLevel && n > 0) {
      // Still report it so the HUD can show the number; just don't bank it.
      this.ctx?.events?.emit?.('xp:gain', { amount: 0, reason, total: this.xp });
      return 0;
    }

    const before = this.level;
    this.xp = Math.max(0, this.xp + n);
    this.match.xp += n;
    this.match.bySource[reason] = (this.match.bySource[reason] ?? 0) + n;
    this._dirty = true;

    this.ctx?.events?.emit?.('xp:gain', { amount: n, reason, total: this.xp });

    const after = levelForXp(this.xp);
    if (after !== before) {
      this.level = after;
      // One event per level crossed: a single kill can carry two levels at low
      // rank, and the UI wants to celebrate both rather than skip one.
      for (let L = before + 1; L <= after; L++) {
        const unlocked = unlocksAtLevel(L).map((u) => this._decorate(u));
        this.match.levels.push(L);
        this._ensureLoadout();
        this.ctx?.events?.emit?.('level:up', { level: L, unlocked });
      }
    }
    return n;
  }

  /** Fills a raw table entry out with the resolved weapon name where relevant. */
  _decorate(entry) {
    if (entry.kind !== 'weapon') return { ...entry };
    const w = this.weapons.find((x) => x.classId === entry.classId);
    return { ...entry, name: w?.name ?? entry.name, weaponId: w?.id ?? entry.weaponId, pending: !!w?.pending };
  }

  /* ------------------------------- readouts -------------------------------- */

  /** XP banked into the current level, and what the current level costs. */
  get xpIntoLevel() {
    return Math.max(0, this.xp - CUMULATIVE[this.level]);
  }
  get xpForNext() {
    return COST[this.level] ?? 0;
  }
  get levelFraction() {
    const need = this.xpForNext;
    return need > 0 ? Math.min(1, this.xpIntoLevel / need) : 1;
  }
  get maxed() {
    return this.level >= TUNING.maxLevel;
  }
  get nextUnlockAt() {
    return nextUnlockLevel(this.level);
  }

  /** A flat object for the UI and the test harness. Allocates; not per-frame. */
  snapshot() {
    return {
      level: this.level,
      xp: this.xp,
      xpIntoLevel: this.xpIntoLevel,
      xpForNext: this.xpForNext,
      fraction: this.levelFraction,
      maxed: this.maxed,
      primary: this.primary,
      secondary: this.secondary,
      nextUnlockAt: this.nextUnlockAt,
      match: { ...this.match, bySource: { ...this.match.bySource } },
    };
  }

  /* -------------------------------- match --------------------------------- */

  /** Start a fresh per-match tally. Career totals are untouched. */
  beginMatch() {
    this.match = newMatchTally();
    this._lifeStreak = 0;
    this._multi = 0;
  }

  /* ------------------------------- lifecycle ------------------------------- */

  update(dt) {
    if (!this._dirty) return;
    this._saveTimer -= dt;
    if (this._saveTimer <= 0) this._flush();
  }

  /* ----------------------------- persistence ------------------------------- */

  _load() {
    let raw = null;
    try {
      raw = localStorage.getItem(TUNING.saveKey);
    } catch {
      // Private browsing, disabled storage, quota — all mean "no save", not "crash".
      this._storageOk = false;
      return;
    }
    this._storageOk = true;
    if (!raw) return;

    let data = null;
    try {
      data = JSON.parse(raw);
    } catch {
      data = null;
    }

    // Anything unexpected — a wrong version, a missing field, a string where a
    // number belongs, an array instead of an object — is discarded in silence.
    // A player whose save is broken gets a fresh start, not an error screen.
    if (!data || typeof data !== 'object' || Array.isArray(data)) return this._discard('shape');
    if (data.v !== TUNING.saveVersion) return this._discard('version');
    const xp = Number(data.xp);
    if (!Number.isFinite(xp) || xp < 0) return this._discard('xp');

    this.xp = Math.min(xp, CUMULATIVE[TUNING.maxLevel]);
    this.level = levelForXp(this.xp);
    if (typeof data.primary === 'string') this.primary = data.primary;
    if (typeof data.secondary === 'string') this.secondary = data.secondary;
  }

  _discard(why) {
    console.warn(`[progression] ignoring unusable save (${why}); starting fresh`);
    try {
      localStorage.removeItem(TUNING.saveKey);
    } catch {
      /* nothing useful to do */
    }
  }

  _flush() {
    this._dirty = false;
    this._saveTimer = TUNING.saveDebounce;
    if (this._storageOk === false) return;
    try {
      localStorage.setItem(
        TUNING.saveKey,
        JSON.stringify({
          v: TUNING.saveVersion,
          xp: this.xp,
          primary: this.primary,
          secondary: this.secondary,
        })
      );
    } catch {
      this._storageOk = false;
    }
  }

  /* --------------------------- debug / harness ----------------------------- */

  /** Jump to a level exactly. XP lands on the level's floor. */
  setLevel(level) {
    const L = clampLevel(level);
    const before = this.level;
    this.xp = CUMULATIVE[L];
    this.level = L;
    this._ensureLoadout();
    this._dirty = true;
    this._flush();
    // One event for the destination, not one per level crossed. Jumping 24 levels
    // from the debug console must not queue 24 callouts at 3.4 s each — that is a
    // minute and a half of UI that outlives whatever you were trying to look at.
    if (L > before) {
      this.ctx?.events?.emit?.('level:up', {
        level: L,
        unlocked: unlocksAtLevel(L).map((u) => this._decorate(u)),
      });
    }
    this.ctx?.events?.emit?.('xp:gain', { amount: 0, reason: 'debug', total: this.xp });
    return this.snapshot();
  }

  /** Everything selectable regardless of level, without touching XP. */
  unlockAll(on = true) {
    this.debugUnlockAll = !!on;
    this.ctx?.events?.emit?.('xp:gain', { amount: 0, reason: 'debug', total: this.xp });
    return this.snapshot();
  }

  reset() {
    this.xp = 0;
    this.level = 1;
    this.debugUnlockAll = false;
    this.primary = null;
    this.secondary = null;
    this.match = newMatchTally();
    this._ensureLoadout();
    this._dirty = true;
    this._flush();
    this._publishLoadout();
    this.ctx?.events?.emit?.('xp:gain', { amount: 0, reason: 'reset', total: 0 });
    return this.snapshot();
  }

  dispose() {
    this._flush();
    this._unregister?.();
    window.removeEventListener('pagehide', this._onHide);
    document.removeEventListener('visibilitychange', this._onHide);
  }
}

/* ---------------------------------------------------------------------------- */

function newMatchTally() {
  return { kills: 0, headshots: 0, deaths: 0, xp: 0, levels: [], bySource: {}, ended: false };
}

function classNameFor(classId) {
  return CLASSES.find((c) => c.id === classId)?.name ?? 'PRIMARY';
}

/**
 * Four normalised bars for the loadout screen. Derived from the weapon definition
 * rather than authored separately so they can never drift from the actual gun.
 * The divisors are display scaling only — nothing reads these back into gameplay.
 */
function statsFor(def) {
  const dmg = def.damage?.[0]?.[1] ?? 0;
  const pellets = def.pellets ?? 1;
  const rpm = def.rpm ?? 0;
  const rangeEnd = def.damage?.[Math.max(0, (def.damage?.length ?? 1) - 2)]?.[0] ?? 0;
  const hipSpread = def.spread?.hip ?? 0.05;
  return {
    damage: clamp01((dmg * pellets) / 180),
    rate: clamp01(rpm / 950),
    range: clamp01(rangeEnd / 140),
    handling: clamp01(1 - hipSpread / 0.09),
    magSize: def.magSize ?? 0,
    fireMode: def.fireMode ?? '—',
  };
}

const clamp01 = (v) => Math.max(0, Math.min(1, v));
